import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import cookie from "react-cookies";
import { getCookieDomain } from "../utils/cookieDomain";

import enTranslations from "./locales/en";
import esTranslations from "./locales/es";

const SUPPORTED_LANGUAGES = ["en-US", "es"];
const DEFAULT_LANGUAGE = "en-US";
const STORAGE_KEY = "app_lang";
const LANGUAGE_COOKIE = "an_lang";
const LANGUAGE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;
const LANGUAGE_ALIASES = {
  en: "en-US",
  "en-us": "en-US",
};

// This module is pulled in by pure utility chains (e.g. getIntlLocale) that are
// exercised under `@jest-environment node`, so every browser API access is guarded
// rather than assumed.
const hasBrowserEnv = typeof window !== "undefined";

const readStoredLanguage = () => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const resolveLanguage = (language) => {
  if (!language) {
    return null;
  }

  const normalizedLanguage = LANGUAGE_ALIASES[language.toLowerCase()] ?? language;
  return SUPPORTED_LANGUAGES.includes(normalizedLanguage) ? normalizedLanguage : null;
};

const persistLanguage = (language) => {
  if (!hasBrowserEnv) {
    return;
  }

  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Ignore storage write failures (blocked/disabled/quota) and proceed.
  }
  cookie.save(LANGUAGE_COOKIE, language, {
    path: "/",
    domain: getCookieDomain(),
    maxAge: LANGUAGE_MAX_AGE_SECONDS,
    secure: process.env.NODE_ENV === "production",
    sameSite: "lax",
  });
};

const setDocumentLanguage = (language) => {
  if (!hasBrowserEnv) {
    return;
  }

  document.documentElement.lang = language;
};

const getInitialLanguage = () => {
  if (!hasBrowserEnv) {
    return DEFAULT_LANGUAGE;
  }

  // 1. Check query parameter ?lang=... (Highest Precedence)
  const params = new URLSearchParams(window.location.search);
  const queryLang = resolveLanguage(params.get("lang"));
  if (queryLang) {
    persistLanguage(queryLang);
    return queryLang;
  }

  // 2. Check shared language cookie (User-specified)
  const cookieLang = resolveLanguage(cookie.load(LANGUAGE_COOKIE));
  if (cookieLang) {
    // Sync localStorage only — do NOT re-save the cookie here. The cookie may be
    // a cross-subdomain `an_lang` set elsewhere; re-writing it with this app's
    // cookie domain could create a host-only cookie that shadows it. We just
    // mirror the value into localStorage.
    try {
      localStorage.setItem(STORAGE_KEY, cookieLang);
    } catch {
      // Ignore storage write failures (blocked/disabled/quota) and proceed.
    }
    return cookieLang;
  }

  // 3. Check local storage to migrate existing preferences
  const savedLang = resolveLanguage(readStoredLanguage());
  if (savedLang) {
    persistLanguage(savedLang);
    return savedLang;
  }

  // 4. Check browser language (Spanish variants)
  const browserLang = navigator.language;
  if (browserLang && browserLang.startsWith("es")) {
    return "es";
  }

  return DEFAULT_LANGUAGE;
};

const initialLanguage = getInitialLanguage();

const resources = {
  en: enTranslations,
  es: esTranslations,
};

i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage,
  // Resource bundles are keyed by base language, so `en-US` reads from `en`.
  load: "languageOnly",
  fallbackLng: "en",
  defaultNS: "common",
  ns: ["common", "auth", "dashboard", "clientHeader", "clientDetails", "rescheduling"],
  interpolation: { escapeValue: false },
});

setDocumentLanguage(initialLanguage);

export const selectLanguage = (language) => {
  const selectedLanguage = resolveLanguage(language);
  if (!selectedLanguage) {
    return;
  }

  persistLanguage(selectedLanguage);
  setDocumentLanguage(selectedLanguage);
  void i18n.changeLanguage(selectedLanguage);
};

export default i18n;
